import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Search, Download, Printer, RefreshCw, ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import pdfMake from 'pdfmake/build/pdfmake';
import { vfs } from '../../fonts/vfs_fonts';
import { TDocumentDefinitions } from 'pdfmake/interfaces';

interface Referee {
  referee_id: string;
  first_name: string;
  last_name: string;
  first_name_tamil?: string;
  last_name_tamil?: string;
  email: string;
  phone?: string;
  is_active: boolean;
  department_id?: string;
  department?: {
    department_id: string;
    name: string;
    name_tamil?: string;
  };
}

interface Department {
  department_id: string;
  name: string;
  name_tamil?: string;
}

// Initialize pdfmake with virtual font files
pdfMake.vfs = vfs;

// Configure fonts for pdfmake
pdfMake.fonts = {
  NotoSansTamil: {
    normal: 'NotoSansTamil-Regular.ttf',
    bold: 'NotoSansTamil-Bold.ttf',
    italics: 'NotoSansTamil-Regular.ttf',
    bolditalics: 'NotoSansTamil-Bold.ttf'
  },
  Roboto: {
    normal: 'Roboto-Regular.ttf',
    bold: 'Roboto-Bold.ttf',
    italics: 'Roboto-Regular.ttf',
    bolditalics: 'Roboto-Bold.ttf'
  }
};

// Helper function to get contrasting text color based on background
const getContrastColor = (hexColor: string): string => {
  // Remove the hash if it exists
  hexColor = hexColor.replace('#', '');
  
  // Parse the color
  const r = parseInt(hexColor.substr(0, 2), 16);
  const g = parseInt(hexColor.substr(2, 2), 16);
  const b = parseInt(hexColor.substr(4, 2), 16);
  
  // Calculate luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  // Return black or white based on luminance
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
};

const RefereeCardGenerator: React.FC = () => {
  const { t, i18n } = useTranslation('common');
  const navigate = useNavigate();
  const [referees, setReferees] = useState<Referee[]>([]);
  const [filteredReferees, setFilteredReferees] = useState<Referee[]>([]);
  const [selectedReferees, setSelectedReferees] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewReferee, setPreviewReferee] = useState<Referee | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [cardColor, setCardColor] = useState('#2563eb'); // Default blue color
  const [departments, setDepartments] = useState<Department[]>([]);
  const [selectedDepartment, setSelectedDepartment] = useState<string>('All');

  useEffect(() => {
    fetchReferees();
    fetchDepartments();
  }, []);

  useEffect(() => {
    filterReferees();
  }, [searchTerm, referees, selectedDepartment]);

  const fetchDepartments = async () => {
    try {
      const { data, error } = await supabase
        .from('departments')
        .select('department_id, name, name_tamil')
        .order('name');
      
      if (error) throw error;
      
      setDepartments(data || []);
    } catch (error: any) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchReferees = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error } = await supabase
        .from('referees')
        .select(`
          referee_id,
          first_name,
          last_name,
          first_name_tamil,
          last_name_tamil,
          email,
          phone,
          is_active,
          department_id,
          department:departments(
            department_id,
            name,
            name_tamil
          )
        `)
        .order('last_name');
      
      if (error) throw error;
      
      setReferees(data || []);
      setFilteredReferees(data || []);
    } catch (error: any) {
      console.error('Error fetching referees:', error);
      setError(error.message || 'Failed to load referees');
    } finally {
      setLoading(false);
    }
  };

  const filterReferees = () => {
    let filtered = referees;
    
    // Apply department filter
    if (selectedDepartment !== 'All') {
      filtered = filtered.filter(r => r.department_id === selectedDepartment);
    }
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(r => 
        `${r.first_name} ${r.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        `${r.first_name_tamil || ''} ${r.last_name_tamil || ''}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (r.phone || '').includes(searchTerm) ||
        (r.department?.name || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setFilteredReferees(filtered);
  };

  const handleSelectAll = () => {
    if (selectedReferees.length === filteredReferees.length) {
      setSelectedReferees([]);
    } else {
      setSelectedReferees(filteredReferees.map(r => r.referee_id));
    }
  };

  const handleSelectReferee = (id: string) => {
    if (selectedReferees.includes(id)) {
      setSelectedReferees(prev => prev.filter(r => r !== id));
    } else {
      setSelectedReferees(prev => [...prev, id]);
    }
  };

  const handlePreview = (referee: Referee) => {
    setPreviewReferee(referee);
    setShowPreview(true);
  };

  const generatePDF = async () => {
    try {
      setGenerating(true);
      
      // Filter selected referees
      const selected = referees.filter(r => selectedReferees.includes(r.referee_id));
      
      if (selected.length === 0) {
        setError(t('referees.cards.selectAtLeastOne'));
        return;
      }
      
      // Create PDF content for each referee
      const content: any[] = [];
      
      selected.forEach((referee, index) => {
        // Add page break after each referee except the first one
        if (index > 0) {
          content.push({ text: '', pageBreak: 'before' });
        }
        
        // Get text color based on background
        const textColor = getContrastColor(cardColor);
        
        // School name at the top
        content.push({
          text: t('referees.cards.schoolName'),
          font: 'NotoSansTamil',
          fontSize: 16,
          bold: true,
          alignment: 'center',
          margin: [0, 0, 0, 5] // Reduced top margin
        });
        
        // Role title with color background
        content.push({
          stack: [
            {
              canvas: [
                {
                  type: 'rect',
                  x: 0,
                  y: 0,
                  w: 515, // A5 landscape width - margins
                  h: 140, // Increased height for larger text
                  r: 5,
                  color: cardColor
                }
              ]
            },
            {
              text: i18n.language === 'ta' ? "நடுவர்" : "REFEREE",
              font: i18n.language === 'ta' ? 'NotoSansTamil' : 'Roboto',
              fontSize: 100, // Large font size
              bold: true,
              color: textColor,
              alignment: 'center',
              margin: [0, -120, 0, 30] // Adjusted margin to center text vertically
            }
          ],
          margin: [0, 0, 0, 15]
        });
        
        // Name (Tamil or English)
        if (referee.first_name_tamil && referee.last_name_tamil) {
          content.push({
            text: `${referee.first_name_tamil} ${referee.last_name_tamil}`,
            font: 'NotoSansTamil',
            fontSize: 30, // Font size for Tamil name
            bold: true,
            alignment: 'center',
            margin: [0, 5, 0, 10]
          });
        } else {
          content.push({
            text: `${referee.first_name} ${referee.last_name}`,
            fontSize: 30, // Font size for English name
            bold: true,
            alignment: 'center',
            margin: [0, 5, 0, 10]
          });
        }
        
        // Department information
        if (referee.department) {
          if (referee.department.name_tamil && i18n.language === 'ta') {
            content.push({
              text: referee.department.name_tamil,
              font: 'NotoSansTamil',
              fontSize: 24,
              alignment: 'center',
              margin: [0, 5, 0, 5]
            });
          } else {
            content.push({
              text: referee.department.name,
              fontSize: 24,
              alignment: 'center',
              margin: [0, 5, 0, 5]
            });
          }
        }
        
        // Contact information
        if (referee.phone) {
          content.push({
            text: referee.phone,
            fontSize: 16,
            alignment: 'center',
            margin: [0, 10, 0, 0]
          });
        }
      });
      
      // Define the document
      const docDefinition: TDocumentDefinitions = {
        pageSize: 'A5',
        pageOrientation: 'landscape',
        pageMargins: [40, 40, 40, 40],
        content: content,
        defaultStyle: {
          font: 'Roboto'
        }
      };
      
      // Generate and download the PDF
      pdfMake.createPdf(docDefinition).download('referee_cards.pdf');
      
    } catch (error: any) {
      console.error('Error generating PDF:', error);
      setError(error.message || 'Failed to generate PDF');
    } finally {
      setGenerating(false);
    }
  };

  const printSingleCard = async (referee: Referee) => {
    try {
      setGenerating(true);
      
      // Get text color based on background
      const textColor = getContrastColor(cardColor);
      
      // Create PDF content for the referee
      const content: any[] = [];
      
      // School name at the top
      content.push({
        text: t('referees.cards.schoolName'),
        font: 'NotoSansTamil',
        fontSize: 16,
        bold: true,
        alignment: 'center',
        margin: [0, 0, 0, 5] // Reduced top margin
      });
      
      // Role title with color background
      content.push({
        stack: [
          {
            canvas: [
              {
                type: 'rect',
                x: 0,
                y: 0,
                w: 515, // A5 landscape width - margins
                h: 140, // Increased height for larger text
                r: 5,
                color: cardColor
              }
            ]
          },
          {
            text: i18n.language === 'ta' ? "நடுவர்" : "REFEREE",
            font: i18n.language === 'ta' ? 'NotoSansTamil' : 'Roboto',
            fontSize: 100, // Large font size
            bold: true,
            color: textColor,
            alignment: 'center',
            margin: [0, -120, 0, 30] // Adjusted margin to center text vertically
          }
        ],
        margin: [0, 0, 0, 15]
      });
      
      // Name (Tamil or English)
      if (referee.first_name_tamil && referee.last_name_tamil) {
        content.push({
          text: `${referee.first_name_tamil} ${referee.last_name_tamil}`,
          font: 'NotoSansTamil',
          fontSize: 30, // Font size for Tamil name
          bold: true,
          alignment: 'center',
          margin: [0, 5, 0, 10]
        });
      } else {
        content.push({
          text: `${referee.first_name} ${referee.last_name}`,
          fontSize: 30, // Font size for English name
          bold: true,
          alignment: 'center',
          margin: [0, 5, 0, 10]
        });
      }
      
      // Department information
      if (referee.department) {
        if (referee.department.name_tamil && i18n.language === 'ta') {
          content.push({
            text: referee.department.name_tamil,
            font: 'NotoSansTamil',
            fontSize: 24,
            alignment: 'center',
            margin: [0, 5, 0, 5]
          });
        } else {
          content.push({
            text: referee.department.name,
            fontSize: 24,
            alignment: 'center',
            margin: [0, 5, 0, 5]
          });
        }
      }
      
      // Contact information
      if (referee.phone) {
        content.push({
          text: referee.phone,
          fontSize: 16,
          alignment: 'center',
          margin: [0, 10, 0, 0]
        });
      }
      
      // Define the document
      const docDefinition: TDocumentDefinitions = {
        pageSize: 'A5',
        pageOrientation: 'landscape',
        pageMargins: [40, 40, 40, 40],
        content: content,
        defaultStyle: {
          font: 'Roboto'
        }
      };
      
      // Generate and open the PDF for printing
      pdfMake.createPdf(docDefinition).print();
      
    } catch (error: any) {
      console.error('Error printing card:', error);
      setError(error.message || 'Failed to print card');
    } finally {
      setGenerating(false);
    }
  };

  // Language selector component
  const LanguageSelector = () => (
    <div className="flex space-x-2">
      <button
        onClick={() => i18n.changeLanguage('en')}
        className={`px-2 py-1 rounded text-sm ${i18n.language === 'en' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
      >
        EN
      </button>
      <button
        onClick={() => i18n.changeLanguage('nn')}
        className={`px-2 py-1 rounded text-sm ${i18n.language === 'nn' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
      >
        NN
      </button>
      <button
        onClick={() => i18n.changeLanguage('ta')}
        className={`px-2 py-1 rounded text-sm ${i18n.language === 'ta' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
      >
        TA
      </button>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <button
            onClick={() => navigate(-1)}
            className="mr-4 inline-flex items-center p-2 border border-transparent rounded-full shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <ArrowLeft className="h-5 w-5" aria-hidden="true" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">{t('referees.cards.title')}</h1>
        </div>
        <div className="flex space-x-3">
          <LanguageSelector />
          <button
            onClick={fetchReferees}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            {t('referees.cards.refresh')}
          </button>
          <button
            onClick={generatePDF}
            disabled={selectedReferees.length === 0 || generating}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-300"
          >
            <Download className="h-4 w-4 mr-2" />
            {generating ? t('referees.cards.generating') : t('referees.cards.generatePDF')}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
          <div className="flex flex-col md:flex-row space-y-3 md:space-y-0 md:space-x-4 md:items-center">
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('referees.cards.searchPlaceholder')}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
            <div className="flex-shrink-0">
              <select
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                <option value="All">{t('referees.filters.allDepartments')}</option>
                {departments.map(dept => (
                  <option key={dept.department_id} value={dept.department_id}>
                    {dept.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex-shrink-0">
              <button
                onClick={handleSelectAll}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                {selectedReferees.length === filteredReferees.length && filteredReferees.length > 0 ? t('referees.cards.deselectAll') : t('referees.cards.selectAll')}
              </button>
            </div>
            <div className="flex-shrink-0 flex items-center">
              <label htmlFor="card-color" className="mr-2 text-sm font-medium text-gray-700">{t('referees.cards.cardColor')}:</label>
              <input
                type="color"
                id="card-color"
                value={cardColor}
                onChange={(e) => setCardColor(e.target.value)}
                className="h-8 w-8 border border-gray-300 rounded-md"
              />
            </div>
          </div>
        </div>
        
        {loading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : filteredReferees.length === 0 ? (
          <div className="text-center py-12">
            <div className="mx-auto h-12 w-12 text-gray-400">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {t('referees.cards.noRefereesFound')}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {t('referees.cards.adjustSearch')}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <input
                      type="checkbox"
                      checked={selectedReferees.length === filteredReferees.length && filteredReferees.length > 0}
                      onChange={handleSelectAll}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('referees.cards.name')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('referees.cards.tamilName')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('referees.cards.department')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('referees.cards.contact')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('referees.cards.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredReferees.map((referee) => (
                  <tr key={referee.referee_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedReferees.includes(referee.referee_id)}
                        onChange={() => handleSelectReferee(referee.referee_id)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {referee.first_name} {referee.last_name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {referee.first_name_tamil} {referee.last_name_tamil}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {referee.department?.name || '-'}
                      </div>
                      {referee.department?.name_tamil && (
                        <div className="text-sm text-gray-500">
                          {referee.department.name_tamil}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {referee.phone || t('referees.cards.noPhone')}
                      </div>
                      <div className="text-xs text-gray-500">
                        {referee.email}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handlePreview(referee)}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                      >
                        {t('referees.cards.preview')}
                      </button>
                      <button
                        onClick={() => printSingleCard(referee)}
                        className="text-green-600 hover:text-green-900"
                        disabled={generating}
                      >
                        <Printer className="h-5 w-5 inline-block" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {showPreview && previewReferee && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-2xl w-full">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">{t('referees.cards.previewTitle')}</h2>
              <button
                onClick={() => setShowPreview(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div 
              className="card border-2 border-gray-300 rounded-lg p-4 flex flex-col items-center"
              style={{ width: '100%', aspectRatio: '210/148' }}
            >
              <div className="text-center text-sm mb-1">
                <span style={{ fontFamily: 'Noto Sans Tamil, Arial, sans-serif' }}>{t('referees.cards.schoolName')}</span>
              </div>
              
              <div 
                className="w-full rounded-lg flex justify-center items-center mb-3"
                style={{ 
                  backgroundColor: cardColor,
                  color: getContrastColor(cardColor),
                  height: '120px'
                }}
              >
                <div className="text-7xl font-bold">
                  {i18n.language === 'ta' ? "நடுவர்" : "REFEREE"}
                </div>
              </div>
              
              {previewReferee.first_name_tamil && previewReferee.last_name_tamil ? (
                <div className="text-3xl mb-3 text-center" style={{ fontFamily: 'Noto Sans Tamil, Arial, sans-serif' }}>
                  {previewReferee.first_name_tamil} {previewReferee.last_name_tamil}
                </div>
              ) : (
                <div className="text-3xl font-semibold mb-3 text-center">
                  {previewReferee.first_name} {previewReferee.last_name}
                </div>
              )}
              
              {previewReferee.department && (
                <div className="text-center">
                  {previewReferee.department.name_tamil && i18n.language === 'ta' ? (
                    <div className="text-2xl mb-1\" style={{ fontFamily: 'Noto Sans Tamil, Arial, sans-serif' }}>
                      {previewReferee.department.name_tamil}
                    </div>
                  ) : (
                    <div className="text-2xl font-bold mb-1">
                      {previewReferee.department.name}
                    </div>
                  )}
                </div>
              )}
              
              {previewReferee.phone && (
                <div className="text-lg mt-2">
                  {previewReferee.phone}
                </div>
              )}
            </div>
            
            <div className="flex justify-end mt-6 space-x-3">
              <button
                onClick={() => printSingleCard(previewReferee)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                disabled={generating}
              >
                <Printer className="h-4 w-4 mr-2" />
                {t('referees.cards.print')}
              </button>
              <button
                onClick={() => {
                  setSelectedReferees([previewReferee.referee_id]);
                  generatePDF();
                }}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                disabled={generating}
              >
                <Download className="h-4 w-4 mr-2" />
                {generating ? t('referees.cards.generating') : t('referees.cards.downloadPDF')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RefereeCardGenerator;